import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, WhatsappLogo, Key } from "@phosphor-icons/react";

export function PinsTab() {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newPin, setNewPin] = useState(null);

  const load = () => api.get("/admin/pins").then(r => setData(r.data)).catch(() => toast.error("Error cargando PINs"));
  useEffect(() => { load(); }, []);

  const createPin = async () => {
    setCreating(true);
    try {
      const { data: res } = await api.post("/admin/pins");
      setNewPin(res.pin);
      toast.success("PIN creado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando PIN");
    } finally { setCreating(false); }
  };

  const copyPin = async (pin) => {
    try { await navigator.clipboard.writeText(pin); toast.success("PIN copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };

  const sharePin = (pin) => {
    const text = encodeURIComponent(
      `🎟️ Tu PIN de acceso para crear tu canción en Tu MusiKa es: *${pin}*\n\n1. Crea tu cuenta: ${window.location.origin}/register\n2. En la pantalla de pago, busca "¿Tienes PIN de acceso?" y escríbelo\n3. ¡Listo, a crear tu canción! 🎵`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const fmtD = (iso) => iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="space-y-5" data-testid="pins-tab">
      <div className="rounded-2xl border border-[#FFB800]/30 bg-[#FFB800]/5 p-5">
        <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-1">
          <Key size={18} weight="fill" className="text-[#FFB800]" /> Crear PIN de acceso para crear una canción
        </h3>
        <p className="text-sm text-white/60 mb-4">
          Cobra en efectivo, genera un PIN único (vale por 1 canción) y dáselo a tu cliente.
          Él lo escribe en la pantalla de pago y crea su canción sin esperar autorización.
        </p>
        <button
          onClick={createPin}
          disabled={creating}
          className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#FFB800] hover:bg-[#E5A600] disabled:opacity-50 text-black font-bold transition-all active:scale-95"
          data-testid="create-pin-btn"
        >
          {creating ? "Generando…" : "🎟️ CREAR PIN DE ACCESO"}
        </button>

        {newPin && (
          <div className="mt-4 rounded-2xl border-2 border-[#B4F34A] bg-[#B4F34A]/10 p-5 text-center" data-testid="new-pin-box">
            <p className="text-xs uppercase tracking-widest text-white/60 mb-2">PIN nuevo · vale por 1 canción</p>
            <p className="font-display font-black text-5xl tracking-[0.25em] text-[#B4F34A]" data-testid="new-pin-value">{newPin}</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              <button onClick={() => copyPin(newPin)} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold flex items-center gap-1.5" data-testid="copy-new-pin">
                <Copy size={14} /> Copiar
              </button>
              <button onClick={() => sharePin(newPin)} className="px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1FB858] text-black text-sm font-semibold flex items-center gap-1.5" data-testid="share-new-pin">
                <WhatsappLogo weight="fill" size={14} /> Enviar al cliente
              </button>
            </div>
          </div>
        )}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="pins-stats">
            <div className="rounded-xl bg-[#121212] border border-white/5 p-3 text-center">
              <p className="font-display font-black text-2xl">{data.total}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Creados</p>
            </div>
            <div className="rounded-xl bg-[#121212] border border-white/5 p-3 text-center">
              <p className="font-display font-black text-2xl text-[#B4F34A]">{data.available}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Disponibles</p>
            </div>
            <div className="rounded-xl bg-[#121212] border border-white/5 p-3 text-center">
              <p className="font-display font-black text-2xl text-white/60">{data.used}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Usados</p>
            </div>
          </div>

          <div className="space-y-2" data-testid="pins-list">
            {data.pins.length === 0 && (
              <p className="text-white/40 text-center py-8">Aún no has creado ningún PIN.</p>
            )}
            {data.pins.map(p => (
              <div key={p.id} className="rounded-xl bg-[#121212] border border-white/5 p-3 flex flex-wrap items-center gap-3" data-testid={`pin-row-${p.pin}`}>
                <span className={`font-mono font-bold tracking-[0.2em] text-lg ${p.used ? "text-white/30 line-through" : "text-[#FFB800]"}`}>
                  {p.pin}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${p.used ? "bg-white/5 text-white/40" : "bg-[#B4F34A]/15 text-[#B4F34A]"}`}>
                  {p.used ? "Usado" : "Disponible"}
                </span>
                <div className="flex-1 min-w-[140px] text-xs text-white/50">
                  Creado: {fmtD(p.created_at)}
                  {p.used && (
                    <span className="block text-white/60">
                      Usado por <span className="text-white/90">{p.used_by_name}</span> ({p.used_by_phone}) · {fmtD(p.used_at)}
                    </span>
                  )}
                </div>
                {!p.used && (
                  <div className="flex gap-1.5">
                    <button onClick={() => copyPin(p.pin)} aria-label="Copiar PIN" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center" data-testid={`copy-pin-${p.pin}`}>
                      <Copy size={13} />
                    </button>
                    <button onClick={() => sharePin(p.pin)} aria-label="Enviar PIN por WhatsApp" className="w-8 h-8 rounded-full bg-[#25D366]/15 hover:bg-[#25D366] text-[#25D366] hover:text-black flex items-center justify-center transition-colors" data-testid={`share-pin-${p.pin}`}>
                      <WhatsappLogo weight="fill" size={13} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
